import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

const MAX_SHIFT = 10;

export default {
  name: "ShiftSetting",
  components: {
    MainLayout,
    Loading,
    flatPickr,
  },

  data() {
    return {
      MAX_SHIFT,
      shifts: [],
      deletedIds: [],
      isLoading: false,
      submitted: false,
      isError: false,
      configDateTime: {
        allowInput: true,
        enableTime: true,
        timeFormat: "h:i K", 
        noCalendar: true, 
        time_24hr: false,
        dateFormat: "H:i",
        enableSeconds: false,
        altInput: true,
      },
    };
  },

  validations: {
    shifts: {
      $each: {
        name: { required, maxLength:maxLength(100) },
        from_time: { required },
        to_time: { required },
        from_breaktime: {
          required: requiredIf(function () {
            return !!this.to_breaktime;
          })
        },

        to_breaktime: {
          required: requiredIf(function () {
            return !!this.from_breaktime;
          })
        }
      }
    }
  },

  mounted() {
    this.fetchShifts();
  },

  methods: {
    // ---------- helpers ----------
    emptyErrors() {
      return {
        name: [],
        from_time: [],
        to_time: [],
        from_breaktime: [],
        to_breaktime: []
      };
    },

    toMinutes(time) {
      if (!time) return null
      const [h, m] = time.split(':').map(Number)
      return h * 60 + m
    },

    defaultShift() {
      return {
        local_id: crypto.randomUUID(),
        name: '',
        from_time: '',
        to_time: '',
        from_breaktime: '',
        to_breaktime: '',
        errors: this.emptyErrors()
      };
    },

    async fetchShifts() {
      this.isLoading = true;
      try {
        const { data } = await axios.get('/shift/setting');
        const shifts = Array.isArray(data?.shifts) ? data.shifts : data;

        if (data?.max_shift !== undefined) {
          this.MAX_SHIFT = data.max_shift;
        }

        this.shifts = shifts?.length
          ? shifts.map(s => ({
              ...s,
              from_time: s.from_time ?? '',
              to_time: s.to_time ?? '',
              from_breaktime: s.from_breaktime ?? '',
              to_breaktime: s.to_breaktime ?? '',
              local_id: crypto.randomUUID(),
              errors: this.emptyErrors()
            }))
          : [this.defaultShift()];

        this.deletedIds = [];
      } catch {
        this.shifts = [this.defaultShift()];
      } finally {
        this.isLoading = false;
      }
    },

    async updateShifts() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.shifts.forEach(s => {
        s.from_time = s.from_time ?? '';
        s.to_time = s.to_time ?? '';
        s.from_breaktime = s.from_breaktime || null;
        s.to_breaktime = s.to_breaktime || null;
      });

      if (this.validateShifts()) {
        this.isError = true;
        return;
      }
      const payloadShifts = this.shifts.map(s => {
        const formatToBackend = (timeStr) => {
          if (!timeStr) return null;
          const [hour, minute] = timeStr.match(/\d+/g);
          let h = parseInt(hour, 10);
          const m = parseInt(minute, 10);
          if (timeStr.toLowerCase().includes('pm') && h < 12) h += 12;
          if (timeStr.toLowerCase().includes('am') && h === 12) h = 0;
          return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
        };

        return {
          id: s.id ?? null,
          name: s.name,
          from_time: formatToBackend(s.from_time),
          to_time: formatToBackend(s.to_time),
          from_breaktime: formatToBackend(s.from_breaktime),
          to_breaktime: formatToBackend(s.to_breaktime),
        };
      });

      const payload = {
        shifts: payloadShifts,
        login_id: Number(this.$store.state.user.id),
        deleted: this.deletedIds
      };

      this.isLoading = true;
      try {
        await axios.post('/shift/setting', payload);
        await this.fetchShifts();
        this.deletedIds = [];
        this.$notification.success('Shifts updated successfully', {
          timer: 3,
          position: 'bottomCenter'
        });
      } catch (e) {
        const msg =
          e.response?.data?.message ||
          'Unable to update shifts. Please try again';
        this.$notification.error(msg, { timer: 3, position: 'bottomCenter' });
      } finally {
        this.isLoading = false;
      }
    },

    addShift() {
      if (this.shifts.length >= this.MAX_SHIFT) return;
      this.shifts.push(this.defaultShift());
    },

    removeShift(shift, index) {
      // UI-only removal; track deleted ids to send on update
      this.isLoading = true;
      try {
        if (shift.id) {
          this.deletedIds.push(shift.id);
        }

        this.shifts.splice(index, 1);

        // Always keep at least 1 card
        if (this.shifts.length === 0) {
          this.shifts.push(this.defaultShift());
        }
      } catch (error) {
        this.$notification.error("Unable to remove shift from the UI. Please try again", {
          timer: 3,
          position: "bottomCenter"
        });
      } finally {
        this.isLoading = false;
      }
    },

    cancelShifts() {
      if (this.$router) {
        this.$router.push({ path: '/' });
      } else {
        window.location.href = '/';
      }
    },

    validateShifts() {
      // reset errors
      this.shifts.forEach(s => (s.errors = this.emptyErrors()));

      // duplicate names
      const nameMap = {};
      this.shifts.forEach(s => {
        const key = s.name?.trim().toLowerCase();
        if (!key) return;
        nameMap[key] = (nameMap[key] || 0) + 1;
      });

      this.shifts.forEach(s => {
        const key = s.name?.trim().toLowerCase();
        if (nameMap[key] > 1) {
          s.errors.name.push('Duplicate shift name');
        }
      });

      // time validations
      this.shifts.forEach(s => {
        const start = this.toMinutes(s.from_time)
        const end = this.toMinutes(s.to_time)
        const bStart = this.toMinutes(s.from_breaktime)
        const bEnd = this.toMinutes(s.to_breaktime)

        if (start !== null && end !== null && start >= end) {
          s.errors.from_time.push('From time must be before To time');
        }

        if (bStart !== null && bEnd === null) {
          s.errors.to_breaktime.push('To break time is required');
        }
        if (bStart === null && bEnd !== null) {
          s.errors.from_breaktime.push('From break time is required');
        }

        if (bStart !== null && bEnd !== null) {
          if (bStart >= bEnd) {
            s.errors.from_breaktime.push('Break start must be before break end')
          }

          if (start !== null && (bStart <= start || bEnd >= end)) {
            s.errors.from_breaktime.push('Break must be within shift period')
          }
        }
      });

      return this.shifts.some(s =>
        Object.values(s.errors).some(e => e.length)
      );
    },

    hasVError(index, field, rule) {
      const vField = this.$v.shifts?.$each?.[index]?.[field];
      return this.submitted && vField?.$error && !vField?.[rule];
    },

    firstApiError(shift, fields = []) {
      if (!shift.errors) return null
      for (const field of fields) {
        if (shift.errors[field]?.length) {
          return shift.errors[field][0]
        }
      }
      return null
    },
  },
};
